/**
 * El cupón del programa de referidos, dicho en una frase.
 *
 * Stripe describe un descuento con cinco campos que no significan nada solos
 * (`amount_off: 2000`, `duration: 'once'`), y el candidato necesita saber dos
 * cosas: cuánto se le quita y por cuánto tiempo. Esta es la regla que traduce
 * lo uno en lo otro, aparte de la pantalla porque es lo único de todo el flujo
 * del cupón que se puede probar sin montar un formulario.
 *
 * El precio rebajado del recuadro lo calcula `discountedPriceCents` (lib/plan),
 * con la misma regla que aplica Stripe; esta frase dice cuánto y por cuánto
 * tiempo.
 */

import { messages } from '~/i18n';

export interface ValidCoupon {
  /** El código tal como lo reconoció Stripe. */
  code: string;
  /** Lo que viaja al checkout para que el descuento ya esté puesto. */
  promotion_code_id: string;
  /** Porcentaje, cuando el cupón es porcentual. */
  percent_off?: number | null;
  /** En CENTAVOS, cuando el cupón es de monto fijo. */
  amount_off?: number | null;
  currency?: string | null;
  duration: 'once' | 'forever' | 'repeating' | string;
  /** Sólo con `duration: 'repeating'`, y no siempre viene. */
  duration_in_months?: number | null;
  /** Quién invitó. Informativo: no se pinta. */
  referrer?: string | null;
  /**
   * El socio del programa detrás del código, si lo hay: con esto la oferta
   * dice en el acto que ese socio va a ver su correo y su plan (Ley 1581).
   */
  partner?: { code: string; partner_name: string | null } | null;
}

/**
 * "USD 20" y no "USD 20.00": el cobro es en dólares redondos y los dos decimales
 * a la derecha se leen como precisión de contador, no como plata. Los centavos
 * sí se escriben cuando existen.
 */
function formatAmount(cents: number, currency: string | null | undefined): string {
  const units = cents / 100;
  const shown = Number.isInteger(units) ? String(units) : units.toFixed(2);
  const code = (currency ?? '').trim().toUpperCase();
  return code ? `${code} ${shown}` : shown;
}

/**
 * Por cuánto tiempo aplica, en la unidad en que se cobra este plan: meses.
 * `repeating` sin `duration_in_months` devuelve vacío — "durante un rato" no le
 * dice nada a nadie y prefiero no decir nada a inventar el plazo.
 */
function describeWindow(coupon: ValidCoupon): string {
  const t = messages().plan.coupon;
  if (coupon.duration === 'once') return t.once;
  if (coupon.duration === 'forever') return t.forever;
  if (coupon.duration === 'repeating') {
    const months = coupon.duration_in_months;
    if (typeof months === 'number' && months > 0) {
      // Un mes es «en tu primer mes», no «durante 1 mes»: así se dice el
      // descuento del socio y el de LANZAMIENTO (`repeating` por 1 mes).
      return months === 1 ? t.firstMonth : t.months(months);
    }
  }
  return '';
}

/**
 * La frase completa que ve el candidato:
 * «Código DANIELA aplicado: −USD 7 en tu primer mes».
 *
 * El signo es el menos tipográfico (−), no el guion: al lado de una cifra el
 * guion se lee como un separador.
 */
export function describeDiscount(coupon: ValidCoupon): string {
  const head = messages().plan.coupon.applied(coupon.code);

  let discount = '';
  if (typeof coupon.amount_off === 'number' && coupon.amount_off > 0) {
    discount = `−${formatAmount(coupon.amount_off, coupon.currency)}`;
  } else if (typeof coupon.percent_off === 'number' && coupon.percent_off > 0) {
    discount = `−${coupon.percent_off}%`;
  }

  // Un cupón sin descuento legible no existe en el contrato, pero si llegara la
  // respuesta correcta es "quedó puesto" y que Stripe muestre el total.
  if (!discount) return head;

  const period = describeWindow(coupon);
  return period ? `${head}: ${discount} ${period}` : `${head}: ${discount}`;
}
